/**
 * ============================================================================
 * SystemConfig 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 SystemConfig 模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createSystemConfigApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config),
 *   post: (url, data, config) => requestClient.post(url, data, config),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createSystemConfigApi } from '@bayes-mes/shared/api/system-config/factory'
 * export const systemConfigApi = createSystemConfigApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await systemConfigApi.getSystemConfigList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createSystemConfigApi } from '@bayes-mes/shared/api/system-config/factory'
 * export const systemConfigApi = createSystemConfigApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器负责返回最终 payload，不要再二次 `.data`
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/system-config/factory
 * @description SystemConfig 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD 方法和特殊方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  SystemConfig,                    // 主实体类型
  SystemConfigQueryParams,          // 列表查询参数
  CreateSystemConfigForm,           // 创建表单
  UpdateSystemConfigForm,           // 更新表单
  SystemConfigStatistics,           // 统计数据
  BatchUpdateConfigsForm,           // 批量更新表单
  BatchUpdateConfigItem,            // 批量更新结果项
  RefreshCacheForm,                 // 刷新缓存表单
} from '../../types/system-config'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

type SystemConfigListPayload = {
  configs: SystemConfig[]
  pagination: {
    current_page: number
    per_page: number
    total: number
    last_page: number
  }
}

type SystemConfigValuePayload = {
  key: string
  value: any
}

type SystemConfigGroupPayload = {
  group: string
  configs: Record<string, any>
}

type BatchUpdateSystemConfigsPayload = {
  results: BatchUpdateConfigItem[]
}

type SystemConfigGroupsPayload = {
  groups: Record<string, string>
}

type SystemConfigTypesPayload = {
  types: Record<string, string>
}

// ===== 导出工厂函数 =====
/**
 * 创建 SystemConfig 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 SystemConfig 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 SystemConfig 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createSystemConfigApi(client: IRequestClient) {
  return {
    // ===== 基础 CRUD 操作 =====
    
    /**
     * 获取系统配置列表（分页）
     * 
     * 使用场景：
     * - 在系统配置管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据分组、状态等条件查询配置
     * 
     * @param params - 查询参数对象（可选）
     *   - search?: string - 搜索关键词，搜索配置键名或描述
     *     示例：search: 'cache'（搜索包含"cache"的配置）
     *   - group?: ConfigGroup | string - 配置分组筛选
     *     示例：group: 'system'（只显示系统配置组）
     *   - is_active?: boolean - 按状态筛选
     *     示例：is_active: true（只显示启用的配置）
     *   - is_system?: boolean - 按配置类型筛选
     *     示例：is_system: true（只显示系统配置）
     *   - page?: number - 页码，从 1 开始，默认 1
     *     示例：page: 1（第一页），page: 2（第二页）
     *   - per_page?: number - 每页数量，默认 15
     *     示例：per_page: 20（每页20条）
     *   - sort_by?: string - 排序字段，默认：sort_order
     *     示例：sort_by: 'created_at'（按创建时间排序）
     *   - sort_order?: 'asc' | 'desc' - 排序方向，默认：asc
     *     示例：sort_order: 'desc'（降序）
     * 
     * @returns Promise<SystemConfigListPayload> 分页结果对象（已解包 payload）
     *   返回的数据结构：
     *   {
     *     configs: SystemConfig[],  // 配置列表数组
     *     pagination: {
     *       current_page: 1,         // 当前页码
     *       per_page: 15,            // 每页数量
     *       total: 158,              // 总记录数
     *       last_page: 11,           // 最后一页页码
     *     }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在配置管理页面选择分组或输入搜索关键词
     * 2. 前端调用：组件调用 systemConfigApi.getSystemConfigList({ group: 'system', search: 'cache' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/system/configs?group=system&search=cache
     * 5. 后端处理：
     *    - 路由匹配到 SystemConfigController@index 方法
     *    - Controller 调用 Service 查询数据库
     *    - 应用筛选条件和分页
     *    - 使用 SystemConfigResource 转换数据（snake_case → camelCase）
     *    - 返回 JSON 响应
     * 6. 适配器解包：将响应数据解包为纯数据对象
     * 7. 前端使用：组件获得类型安全的配置列表数据
     * 
     * @example
     * ```typescript
     * // 基础用法
     * const result = await systemConfigApi.getSystemConfigList()
     * 
     * // 带筛选
     * const result = await systemConfigApi.getSystemConfigList({
     *   group: 'system',
     *   is_active: true,
     *   page: 1,
     *   per_page: 20
     * })
     * 
     * // 搜索配置
     * const result = await systemConfigApi.getSystemConfigList({
     *   search: 'cache',
     *   sort_by: 'config_key',
     *   sort_order: 'asc'
     * })
     * ```
     */
    getSystemConfigList(params?: SystemConfigQueryParams) {
      return client.get<SystemConfigListPayload>(API_ROUTES.systemConfigs.list, {
        params,
      })
    },
    
    /**
     * 获取单个系统配置的值
     * 
     * 使用场景：
     * - 业务代码中读取特定配置项的值
     * - 检查配置是否存在
     * - 获取配置的当前值（已解密、已格式化）
     * 
     * @param key - 配置键名
     *   格式：group.key
     *   示例：'system.cache_enabled', 'order.order_number_prefix'
     * 
     * @returns Promise<SystemConfigValuePayload> 配置键值对（已解包 payload）
     *   返回的数据结构：
     *   {
     *     key: 'system.cache_enabled',  // 配置键名
     *     value: true,                  // 配置值（已格式化）
     *   }
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.getSystemConfig('system.cache_enabled')
     * 2. 发送请求：GET /api/v1/system/configs/system.cache_enabled
     * 3. 后端处理：
     *    - SystemConfigController@show 方法
     *    - 调用 SystemConfigService::get() 方法
     *    - 优先级：进程缓存 > Redis > 数据库 > Config文件
     *    - 返回格式化后的值（已解密、已类型转换）
     * 4. 前端获得：类型安全的配置值
     * 
     * @example
     * ```typescript
     * // 获取缓存开关配置
     * const result = await systemConfigApi.getSystemConfig('system.cache_enabled')
     * if (result.value === true) {
     *   // 缓存已启用
     * }
     * 
     * // 获取订单号前缀配置
     * const orderPrefix = await systemConfigApi.getSystemConfig('order.order_number_prefix')
     * console.log(orderPrefix.value) // 'SO'
     * ```
     */
    getSystemConfig(key: string) {
      return client.get<SystemConfigValuePayload>(
        API_ROUTES.systemConfigs.detail(encodeURIComponent(key))
      )
    },
    
    /**
     * 获取配置分组的所有配置
     * 
     * 使用场景：
     * - 加载某个分组的全部配置
     * - 批量读取分组配置用于初始化
     * - 配置页面按分组标签页展示
     * 
     * @param group - 分组名称
     *   示例：'system', 'order', 'customer'
     * 
     * @returns Promise<SystemConfigGroupPayload> 分组配置（已解包 payload）
     *   返回的数据结构：
     *   {
     *     group: 'system',
     *     configs: {
     *       cache_enabled: true,
     *       cache_ttl: 3600,
     *       // ... 其他配置
     *     }
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.getSystemConfigGroup('system')
     * 2. 发送请求：GET /api/v1/system/configs/groups/system
     * 3. 后端处理：
     *    - SystemConfigController@getGroup 方法
     *    - 调用 SystemConfigService::getGroup() 方法
     *    - 从Redis/数据库获取分组的所有配置
     *    - 返回键值对对象
     * 4. 前端获得：分组的所有配置
     * 
     * @example
     * ```typescript
     * // 获取系统分组的所有配置
     * const result = await systemConfigApi.getSystemConfigGroup('system')
     * const configs = result.configs
     * 
     * // 使用配置
     * if (configs.cache_enabled) {
     *   console.log('缓存已启用，TTL:', configs.cache_ttl)
     * }
     * ```
     */
    getSystemConfigGroup(group: string) {
      return client.get<SystemConfigGroupPayload>(
        API_ROUTES.systemConfigs.configsGroups(encodeURIComponent(group))
      )
    },
    
    /**
     * 创建系统配置
     * 
     * 使用场景：
     * - 添加新的系统配置项
     * - 批量初始化配置
     * 
     * 权限要求：system:config:create
     * 
     * @param data - 创建表单数据
     *   必填字段：
     *   - config_key: string - 配置键名，唯一，格式：group.key
     *   - config_value: any - 配置值
     *   - config_type: ConfigType - 配置类型
     *   - config_group: ConfigGroup | string - 配置分组
     *   
     *   可选字段：
     *   - config_description?: string - 配置描述
     *   - is_encrypted?: boolean - 是否加密，默认false
     *   - is_cached?: boolean - 是否缓存，默认true
     *   - cache_ttl?: number - 缓存时间（秒），默认3600
     *   - is_system?: boolean - 是否系统配置，默认false
     *   - is_active?: boolean - 是否启用，默认true
     *   - sort_order?: number - 排序序号，默认0
     * 
     * @returns Promise<void> 创建结果（无业务 payload）
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.createSystemConfig({ ... })
     * 2. 发送请求：POST /api/v1/system/configs
     * 3. 后端处理：
     *    - SystemConfigController@store 方法
     *    - CreateSystemConfigRequest 验证数据
     *    - SystemConfigService::set() 创建配置
     *    - 同步到数据库、Redis、进程缓存
     *    - 记录审计日志
     * 4. 前端获得：创建结果
     * 
     * @example
     * ```typescript
     * await systemConfigApi.createSystemConfig({
     *   config_key: 'system.new_feature_enabled',
     *   config_value: true,
     *   config_type: ConfigType.BOOLEAN,
     *   config_group: ConfigGroup.SYSTEM,
     *   config_description: '新功能开关',
     *   is_cached: true,
     *   cache_ttl: 3600,
     *   is_active: true
     * })
     * ```
     */
    createSystemConfig(data: CreateSystemConfigForm) {
      return client.post<void>(API_ROUTES.systemConfigs.configs, data)
    },
    
    /**
     * 更新系统配置
     * 
     * 使用场景：
     * - 修改配置值
     * - 更新配置描述
     * - 启用/禁用配置
     * 
     * 权限要求：system:config:update
     * 
     * 注意事项：
     * - 系统配置不允许修改：config_type, config_group, is_system, is_encrypted
     * - 更新后会自动同步到所有缓存层
     * - 会记录审计日志
     * 
     * @param key - 配置键名
     * @param data - 更新数据（所有字段可选，部分更新）
     * 
     * @returns Promise<void> 更新结果（无业务 payload）
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.updateSystemConfig(key, { ... })
     * 2. 发送请求：PUT /api/v1/system/configs/:key
     * 3. 后端处理：
     *    - SystemConfigController@update 方法
     *    - UpdateSystemConfigRequest 验证数据
     *    - 检查系统配置的字段限制
     *    - SystemConfigService::set() 更新配置
     *    - 同步到数据库、Redis、进程缓存
     *    - 记录审计日志
     * 4. 前端获得：更新结果
     * 
     * @example
     * ```typescript
     * // 修改配置值
     * await systemConfigApi.updateSystemConfig('system.cache_enabled', {
     *   config_value: false,
     *   config_description: '暂时禁用缓存'
     * })
     * 
     * // 禁用配置
     * await systemConfigApi.updateSystemConfig('system.old_feature', {
     *   is_active: false
     * })
     * ```
     */
    updateSystemConfig(key: string, data: UpdateSystemConfigForm) {
      return client.put<void>(
        API_ROUTES.systemConfigs.detail(encodeURIComponent(key)),
        data
      )
    },
    
    /**
     * 删除系统配置
     * 
     * 使用场景：
     * - 删除不再使用的配置项
     * 
     * 权限要求：system:config:delete
     * 
     * 注意事项：
     * - 系统配置（is_system=true）不允许删除
     * - 删除后会从所有缓存层移除
     * - 会记录审计日志
     * 
     * @param key - 配置键名
     * 
     * @returns Promise<void> 删除结果（无业务 payload）
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.deleteSystemConfig(key)
     * 2. 发送请求：DELETE /api/v1/system/configs/:key
     * 3. 后端处理：
     *    - SystemConfigController@destroy 方法
     *    - 检查是否为系统配置
     *    - SystemConfigService::delete() 删除配置
     *    - 从数据库、Redis、进程缓存中删除
     *    - 记录审计日志
     * 4. 前端获得：删除结果
     * 
     * @example
     * ```typescript
     * await systemConfigApi.deleteSystemConfig('custom.temp_config')
     * ```
     */
    deleteSystemConfig(key: string) {
      return client.delete<void>(
        API_ROUTES.systemConfigs.detail(encodeURIComponent(key))
      )
    },
    
    // ===== 特殊操作 =====
    
    /**
     * 批量更新系统配置
     * 
     * 使用场景：
     * - 一次性更新多个配置项
     * - 配置页面的"保存全部"功能
     * - 批量初始化配置
     * 
     * 权限要求：system:config:update
     * 
     * @param data - 批量更新数据
     *   configs: Array<{ key: string; value: any }> - 配置项数组
     * 
     * @returns Promise<BatchUpdateSystemConfigsPayload> 批量更新结果（已解包 payload）
     *   每个配置项的更新结果：
     *   {
     *     key: 'system.cache_enabled',
     *     success: true
     *   }
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.batchUpdateSystemConfigs({ configs: [...] })
     * 2. 发送请求：POST /api/v1/system/configs/batch/update
     * 3. 后端处理：
     *    - SystemConfigController@batchUpdate 方法
     *    - 验证数据
     *    - 循环调用 SystemConfigService::set() 更新每个配置
     *    - 返回每个配置的更新结果
     * 4. 前端获得：批量更新结果
     * 
     * @example
     * ```typescript
     * const result = await systemConfigApi.batchUpdateSystemConfigs({
     *   configs: [
     *     { key: 'system.cache_enabled', value: true },
     *     { key: 'system.cache_ttl', value: 7200 },
     *     { key: 'order.order_number_prefix', value: 'SO' }
     *   ]
     * })
     * 
     * // 检查结果
     * result.results.forEach(item => {
     *   if (item.success) {
     *     console.log(`配置 ${item.key} 更新成功`)
     *   } else {
     *     console.error(`配置 ${item.key} 更新失败`)
     *   }
     * })
     * ```
     */
    batchUpdateSystemConfigs(data: BatchUpdateConfigsForm) {
      return client.post<BatchUpdateSystemConfigsPayload>(
        API_ROUTES.systemConfigs.update,
        data
      )
    },
    
    /**
     * 刷新配置缓存
     * 
     * 使用场景：
     * - 手动刷新配置缓存
     * - 配置更新后强制刷新
     * - 清除缓存问题的配置
     * 
     * 权限要求：system:config:update
     * 
     * @param data - 刷新参数（可选）
     *   - key?: string - 配置键名，不传则刷新所有缓存
     * 
     * @returns Promise<void> 刷新结果（无业务 payload）
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.refreshSystemConfigCache({ key: '...' })
     * 2. 发送请求：POST /api/v1/system/configs/cache/refresh
     * 3. 后端处理：
     *    - SystemConfigController@refreshCache 方法
     *    - SystemConfigService::refreshCache() 刷新缓存
     *    - 从Redis和进程缓存中删除配置
     *    - 重新加载配置到缓存
     * 4. 前端获得：刷新结果
     * 
     * @example
     * ```typescript
     * // 刷新单个配置缓存
     * await systemConfigApi.refreshSystemConfigCache({ 
     *   key: 'system.cache_enabled' 
     * })
     * 
     * // 刷新所有配置缓存
     * await systemConfigApi.refreshSystemConfigCache()
     * ```
     */
    refreshSystemConfigCache(data?: RefreshCacheForm) {
      return client.post<void>(
        API_ROUTES.systemConfigs.refresh,
        data || {}
      )
    },
    
    /**
     * 预加载配置到内存缓存
     * 
     * 使用场景：
     * - 系统启动时预加载高频配置
     * - 缓存预热
     * - 提高配置读取性能
     * 
     * 权限要求：system:config:update
     * 
     * @returns Promise<void> 预加载结果（无业务 payload）
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.preloadSystemConfigCache()
     * 2. 发送请求：POST /api/v1/system/configs/cache/preload
     * 3. 后端处理：
     *    - SystemConfigController@preloadCache 方法
     *    - SystemConfigService::preloadProcessCache() 预加载
     *    - 将高频配置加载到进程缓存
     * 4. 前端获得：预加载结果
     * 
     * @example
     * ```typescript
     * await systemConfigApi.preloadSystemConfigCache()
     * ```
     */
    preloadSystemConfigCache() {
      return client.post<void>(
        API_ROUTES.systemConfigs.preload
      )
    },
    
    /**
     * 获取系统配置统计信息
     * 
     * 使用场景：
     * - 配置管理页面的统计展示
     * - 系统监控面板
     * - 缓存性能监控
     * 
     * @returns Promise<SystemConfigStatistics> 统计信息（已解包 payload）
     *   包含：
     *   - totalConfigs: 配置总数
     *   - activeConfigs: 启用的配置数
     *   - cachedConfigs: 可缓存的配置数
     *   - cacheEnabled: 是否启用缓存
     *   - processCache: 进程缓存统计（命中率等）
     *   - phpMemoryUsage: PHP内存使用(MB)
     *   - phpMemoryPeak: PHP内存峰值(MB)
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.getSystemConfigStats()
     * 2. 发送请求：GET /api/v1/system/configs/stats/info
     * 3. 后端处理：
     *    - SystemConfigController@stats 方法
     *    - SystemConfigService::getStats() 获取统计
     *    - 查询数据库统计配置数量
     *    - 获取缓存统计信息
     *    - 获取内存使用情况
     * 4. 前端获得：统计信息
     * 
     * @example
     * ```typescript
     * const result = await systemConfigApi.getSystemConfigStats()
     * const stats = result
     * 
     * console.log(`配置总数: ${stats.totalConfigs}`)
     * console.log(`启用配置: ${stats.activeConfigs}`)
     * console.log(`缓存命中率: ${
     *   stats.processCache.hits / 
     *   (stats.processCache.hits + stats.processCache.misses) * 100
     * }%`)
     * ```
     */
    getSystemConfigStats() {
      return client.get<SystemConfigStatistics>(
        API_ROUTES.systemConfigs.statsInfo
      )
    },
    
    /**
     * 获取配置分组列表
     * 
     * 使用场景：
     * - 配置管理页面的分组标签页
     * - 显示每个分组的配置数量
     * 
     * @returns Promise<SystemConfigGroupsPayload> 分组列表（已解包 payload）
     *   每个分组包含：
     *   - name: 分组名称
     *   - total: 总配置数
     *   - active: 启用的配置数
     *   - inactive: 禁用的配置数
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.getSystemConfigGroups()
     * 2. 发送请求：GET /api/v1/system/configs/groups/list
     * 3. 后端处理：
     *    - SystemConfigController@getGroups 方法
     *    - SystemConfig::getConfigGroups() 获取分组标签
     *    - 返回分组列表
     * 4. 前端获得：分组列表
     * 
     * @example
     * ```typescript
     * const result = await systemConfigApi.getSystemConfigGroups()
     * const groups = result.groups
     * 
     * // 渲染分组标签页
     * groups.forEach(group => {
     *   console.log(`${group.name}: ${group.active}/${group.total}`)
     * })
     * ```
     */
    getSystemConfigGroups() {
      return client.get<SystemConfigGroupsPayload>(
        API_ROUTES.systemConfigs.groupsList
      )
    },
    
    /**
     * 获取配置类型列表
     * 
     * 使用场景：
     * - 配置表单的类型选择下拉框
     * - 显示配置类型的中文标签
     * 
     * @returns Promise<SystemConfigTypesPayload> 类型列表（已解包 payload）
     *   键值对：
     *   {
     *     string: '字符串',
     *     integer: '整数',
     *     ...
     *   }
     * 
     * 数据流向：
     * 1. 前端调用：systemConfigApi.getSystemConfigTypes()
     * 2. 发送请求：GET /api/v1/system/configs/types/list
     * 3. 后端处理：
     *    - SystemConfigController@getTypes 方法
     *    - SystemConfig::getConfigTypes() 获取类型标签
     *    - 返回类型列表
     * 4. 前端获得：类型列表
     * 
     * @example
     * ```typescript
     * const result = await systemConfigApi.getSystemConfigTypes()
     * const types = result.types
     * 
     * // 渲染类型选择器
     * Object.entries(types).forEach(([value, label]) => {
     *   console.log(`<option value="${value}">${label}</option>`)
     * })
     * ```
     */
    getSystemConfigTypes() {
      return client.get<SystemConfigTypesPayload>(
        API_ROUTES.systemConfigs.typesList
      )
    },
  }
}
